import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {invert} from 'underscore';

import {getSchemaPoliciesSectionURL} from 'auth/PolicyDAO';
import BenchlingConfig from 'BenchlingConfig';
import {setWindowTitle} from 'BenchlingUtil';
import navigateToURL from 'lib/navigateToURL';
import {smallSpacingTop} from 'styles/Utilities';
import {GRAY_LIGHT} from 'ui-library/Colors';
import {Header1} from 'ui-library/Headers';
import SectionTabs from 'ui-library/SectionTabs';

import SchemaPoliciesPoliciesTab from './SchemaPoliciesPoliciesTab';
import SchemaPoliciesSettingsTab from './SchemaPoliciesSettingsTab';

export enum SchemaPoliciesPageTab {
  POLICIES_TAB = 'POLICIES_TAB',
  SETTINGS_TAB = 'SETTINGS_TAB',
}

// Keep in sync with benchling/auth/views.py
export const SCHEMA_POLICIES_PAGE_SECTION_URL_PARAM_TO_TAB = {
  policies: SchemaPoliciesPageTab.POLICIES_TAB,
  settings: SchemaPoliciesPageTab.SETTINGS_TAB,
};

export const SCHEMA_POLICIES_PAGE_TAB_TO_URL_PARAM = invert(SCHEMA_POLICIES_PAGE_SECTION_URL_PARAM_TO_TAB);

const TAB_TO_DISPLAY: {[key in SchemaPoliciesPageTab]: string} = {
  [SchemaPoliciesPageTab.POLICIES_TAB]: 'Policies',
  [SchemaPoliciesPageTab.SETTINGS_TAB]: 'Settings',
};

export interface SchemaPoliciesPageProps {
  activeTab: SchemaPoliciesPageTab;
}

export default function SchemaPoliciesPage(props: SchemaPoliciesPageProps): JSX.Element {
  React.useEffect(() => {
    setWindowTitle('Schema Access Policies');
  }, []);

  const enabledTabs = [SchemaPoliciesPageTab.POLICIES_TAB];
  if (BenchlingConfig.PERMISSIONS_ENABLE_SCHEMA_ACCESS_POLICIES_SETTINGS_TAB) {
    enabledTabs.push(SchemaPoliciesPageTab.SETTINGS_TAB);
  }

  const tabContent = (() => {
    switch (props.activeTab) {
      case SchemaPoliciesPageTab.POLICIES_TAB:
        return <SchemaPoliciesPoliciesTab />;
      case SchemaPoliciesPageTab.SETTINGS_TAB:
        return <SchemaPoliciesSettingsTab />;
      default:
        return null;
    }
  })();
  return (
    <SchemaPoliciesPageContainer>
      {enabledTabs.length > 1 ? (
        <div className={css(styles.tabContainer)}>
          <SectionTabs
            className={css(styles.sectionTabs)}
            tabs={Object.keys(TAB_TO_DISPLAY).map((schemaPoliciesPageTab: SchemaPoliciesPageTab) => ({
              handleClick: () => {
                navigateToURL(
                  getSchemaPoliciesSectionURL(SCHEMA_POLICIES_PAGE_TAB_TO_URL_PARAM[schemaPoliciesPageTab])
                );
              },
              text: TAB_TO_DISPLAY[schemaPoliciesPageTab],
              isActive: props.activeTab === schemaPoliciesPageTab,
            }))}
          />
          <div className={css(styles.bodyContainer)}>{tabContent}</div>
        </div>
      ) : (
        tabContent
      )}
    </SchemaPoliciesPageContainer>
  );
}

function SchemaPoliciesPageContainer(props: {children: React.ReactNode}): JSX.Element {
  return (
    <div className="settingsPage">
      <div className="settingsPage-contentWrapper">
        <Header1>Schema access policies</Header1>
        {props.children}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  bodyContainer: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
    paddingTop: '29px',
  },
  sectionTabs: smallSpacingTop,
  tabContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
});
